import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import { Importer, TextImporterFactory } from '@wisemapping/editor';
import React, { useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import { ErrorInfo } from '../../classes/client';
import Input from '../form/input';
import { ClientContext } from '../../classes/provider/client-context';
import FormContainer from '../layout/form-container';
import { useNavigate } from 'react-router-dom';
import AppConfig from '../../classes/app-config';

export type ImportModel = {
  title: string;
  description?: string;
  contentType?: string;
  content?: null | string;
};

type ErrorFile = {
  error: boolean;
  message: string;
};

const defaultModel: ImportModel = { title: '' };

const ImportMap = (): React.ReactElement => {
  const client = useContext(ClientContext);
  const [model, setModel] = useState<ImportModel>(defaultModel);
  const [error, setError] = useState<ErrorInfo>();
  const [errorFile, setErrorFile] = useState<ErrorFile>({ error: false, message: '' });
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);;
  const [importedMapId, setImportedMapId] = useState<number | null>(null);
  const intl = useIntl();
  const navigate = useNavigate();

  const mutation = useMutation<number, ErrorInfo, ImportModel>(
    (model: ImportModel) => {
      console.log("Sending import request with model:", model);
      return client.importMap(model);
    },
    {
      onSuccess: (mapId: number) => {
        //console.log("Import successful, received mapId:", mapId, "type:", typeof mapId);

        // Ensure mapId is a valid number
        if (mapId === undefined || mapId === null || isNaN(mapId)) {
          setSuccess(true);
          setLoading(false);
          setImportedMapId(mapId);
          setModel(defaultModel);
        } else {
          window.parent.location.href = `${AppConfig.getUiBaseUrl()}/c/maps/${mapId}/edit/`;
        }
      },
      onError: (error) => {
        console.error("Import error:", error);
        setLoading(false);
        setError(error);
        setSuccess(false);
      },
    },
  );

  const handleOnSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setLoading(true);
    setSuccess(false);
    setError(undefined);
    mutation.mutate(model);

  };

  const handleNewImport = (): void => {
    setSuccess(false);
    setError(undefined);
    setErrorFile({ error: false, message: '' });
    setModel(defaultModel);
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    event.preventDefault();

    const name = event.target.name;
    const value = event.target.value;
    setModel({ ...model, [name as keyof ImportModel]: value });
  };

  const handleOnFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event?.target?.files;
    const reader = new FileReader();

    if (files) {
      const file = files[0];
      // Closure to capture the file information.
      reader.onload = (event) => {
        // Suggest file name ...
        const fileName = file.name;
        if (fileName) {
          const title = fileName.split('.')[0];
          if (!model.title || 0 === model.title.length) {
            model.title = title;
          }
        }

        const extensionFile = file.name.split('.').pop();
        const extensionAccept = ['wxml', 'mm'];

        if (!extensionFile || !extensionAccept.includes(extensionFile)) {
          setErrorFile({
            error: true,
            message: intl.formatMessage(
              {
                id: 'import.error-file1',
                defaultMessage: 'Erreur d importation {error}',
              },
              {
                //error: 'You can import WiseMapping and Freemind maps to your list of maps. Select the file you want to import.',
                error: 'Vous pouvez importer des maps WiseMapping et Freemind dans votre liste de maps. Sélectionnez le fichier à importer.',
              },
            ),
          });
        }

        model.contentType = 'application/xml';

        const fileContent = event?.target?.result;
        const mapContent: string | undefined =
          typeof fileContent === 'string' ? fileContent : fileContent?.toString();

        try {
          const importer: Importer = TextImporterFactory.create(
            extensionFile,
            mapContent ? mapContent : '',
          );

          importer.import(model.title, model.description).then((res) => {
            model.content = res;
            setModel({ ...model });
          });
        } catch (e) {
          if (e instanceof Error) {
            setErrorFile({
              error: true,
              message: intl.formatMessage(
                {
                  id: 'import.error-file1',
                  defaultMessage: 'Erreur d importation {error}',
                },
                {
                  error: e.message,
                },
              ),
            });
          }
        }
      };

      // Read in the image file as a data URL.
      reader.readAsText(file);
    }
  };

  return (
    <div>
      <FormContainer>
        <h1>
          <FormattedMessage id="import.title1" defaultMessage="Importer la map" />
        </h1>

        {success && (
          <Alert severity="success" style={{ marginBottom: '20px' }}>
            <p>
              <FormattedMessage
                id="import.success1"
                defaultMessage="La map a été importée avec succès!"
              />
            </p>
            <p>Map ID: {importedMapId !== null ? importedMapId : 'Not available'}</p>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNewImport}
              >
                <FormattedMessage id="import.new1" defaultMessage="Importer une autre map" />
              </Button>
              {importedMapId && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => navigate(`/c/maps/${importedMapId}/edit`)}
                >
                  <FormattedMessage id="import.edit" defaultMessage="Éditer la map" />
                </Button>
              )}
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate('/c/maps')}
              >
                <FormattedMessage id="import.view-all" defaultMessage="Voir toutes les maps" />
              </Button>
            </div>
          </Alert>
        )}

        {!success && (
          <form onSubmit={handleOnSubmit}>
            {errorFile.error && (
              <Alert severity="error">
                <p>{errorFile.message}</p>
              </Alert>
            )}

            {error && (
              <Alert severity="error">
                <p>{error.msg}</p>
              </Alert>
            )}

            <FormControl fullWidth={true}>
              <input
                accept=".wxml,.mm"
                id="contained-button-file"
                type="file"
                required={true}
                style={{ display: 'none' }}
                onChange={handleOnFileChange}
              />

              <Input
                name="title"
                type="text"
                label={intl.formatMessage({
                  id: 'action.rename-name-placeholder1',
                  defaultMessage: 'Nom',
                })}
                value={model.title}
                onChange={handleOnChange}
                error={error}
                fullWidth={true}
              />

              <Input
                name="description"
                type="text"
                label={intl.formatMessage({
                  id: 'action.rename-description-placeholder1',
                  defaultMessage: 'Description',
                })}
                value={model.description}
                onChange={handleOnChange}
                required={false}
                fullWidth={true}
              />

              <label htmlFor="contained-button-file">
                <Button
                  variant="outlined"
                  component="span"
                  style={{ margin: '10px 5px', width: '100%', color: '#82b440', borderColor: '#82b440' }}
                >
                  <FormattedMessage id="maps.choose-file1" defaultMessage="Choisissez un fichier" />
                </Button>
              </label>

              <FormattedMessage id="import.title1" defaultMessage="Les formats de fichiers autorisés sont WXML ou MM" />

              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <Button
                  variant="contained"
                  type="submit"
                  style={{ margin: '10px 5px', width: '100%', backgroundColor: '#82b440' }}
                  disabled={!model.content || !model.title || loading}
                >
                  <FormattedMessage id="import.button1" defaultMessage={loading ? 'Importation de Map...' : 'Créer'} />
                </Button>
              </div>
            </FormControl>
          </form>
        )}
      </FormContainer>
    </div>
  );
};

export default ImportMap;